import { Commit, createStore } from "vuex";
import createPersistedState from "vuex-persistedstate";

import { containerWithSliderModule } from "@/widgets/ContainerWithSlider";
import { installerTableModule } from "@/widgets/installers/InstallerScheduleTable/model/module/tableModule";
import type { IOutsourcer } from "@/entities/outsourcer";

import { getTask, getTaskComments } from "../api/task_api";
import { getUserInfoById, getUsersDepartments } from "../api/user_api";
import authorize from "../entities/user";

import { TooManyRequestsError } from "@/core/errors/api_errors";
import { preserveEditingFields, type EditingFieldRef } from "@/core/preserveEditingFields";
import { createLatinHash, getAllFieldsErrors, replaceHtmlEntities, updateTitle } from "@/core/utils";
import { DeepSearchQueryParams, DesktopVersion, FieldValue, ListElementType } from "@/domain/basic";
import { CatalogDataType, CatalogDataTypeWithTime, CatalogListElementType } from "@/domain/catalog";
import { StoreCompany } from "@/domain/company";
import { FieldErrors, FieldsType, FieldsTypeValue, FormDataType } from "@/domain/form";
import {
	RegistryFieldFilter,
	RegistryFieldSorting,
	RegistryFilterParamsKeys,
	RegistrySetting,
	RegistrySortType,
} from "@/domain/registry";
import {
	ChangeField,
	StoreNotification,
	TaskAttachment,
	TaskComment,
	TaskCommentsPayload,
	TaskDataType,
	TaskListCache,
	TaskListElement,
	TaskSpectatorType,
} from "@/domain/task";
import { Bot, Department, FullUser, PostBotData, User, UserSettings } from "@/domain/user";
import { PpaDate } from "@/domain/ppa";

export interface State {
	token: string;
	catalogs: CatalogDataTypeWithTime[];
	catalogHeader: CatalogListElementType[];
	taskList: TaskListElement[];
	task: TaskDataType;
	users: User[];
	bots: Bot[];
	departments: Department[];
	isError: boolean;
	currentTaskParams: string;
	currentUser: CurrentUser | undefined;
	comments: TaskComment[];
	formList: { [id: number]: ListElementType };
	isNotificationGranted: boolean;
	formFields: FieldsTypeValue[];
	currentUserInfo: FullUser | null;
	appReady: boolean;
	forwardPathName: string;
	currentMobileLocation: string;
	forms: ListElementType[];
	unreadTaskCounter: number;
	isCollapseMenu: boolean;
	queryParams: DeepSearchQueryParams;
	deepSearchQueryParams: DeepSearchQueryParams;
	deepSearchToggle: boolean;
	showSystemMessages: boolean;
	registrySettings: RegistrySetting[];
	registryFilters: RegistryFieldFilter[];
	registryCurrentFormId: number | null;
	registrySort: RegistryFieldSorting | null;
	registrySortType: RegistrySortType;
	registryQueryParams: DeepSearchQueryParams;
	isRegistryLoading: boolean;
	taskLeftScreen: string;
	taskRightScreen: string;
	deepSearchTabs: string[];
	isDeepSearched: boolean;
	tasksIdsForRead: number[];
	appTitle: string;
	form: FormDataType<FieldsType> | null;
	fieldsErrors: FieldErrors[];
	theme: "dark" | "light";
	userSettings: UserSettings | null;
	taskListCache: TaskListCache;
	taskChangedFields: ChangeField[];
	editingFields: EditingFieldRef[];
	timestamp: null | number;
	scrollPosition: number;
	storeCompany: StoreCompany;
	notification: StoreNotification;
	warningSuccess: boolean;
	images: Record<string, string>;
	fromRegistry: boolean;
	isNewRowBlocked: boolean;
	taskSplitWidth: number | null;
	outsourceList: IOutsourcer[];
	isMobile: boolean;
	mobileMenuIsOpen: boolean;
	desktopVersion: DesktopVersion;
	PpaData: PpaDate | null;
	status: string;

	// Модули
	installerTable?: unknown;
	containerWithSlider?: unknown;
	installerSchedule?: unknown;
}

export type CurrentUser = {
	login: string;
	id: number;
};

export interface LoginDto {
	login: string;
	password: string;
}

const emptyTask = {
	attachments: [],
	id: 0,
	name: [],
	form_id: 0,
	form_name: "",
	date: 0,
	closed: false,
	step_id: 0,
	pinned_links: [],
	fields: [],
	steps: [],
	spectators: {
		users: [],
		departments: [],
	},
	permissions: {
		get_task: false,
		create_task: false,
		edit_task: false,
		open_task: false,
		close_task: false,
		write_comments: false,
		add_task_spectator: false,
		delete_task_spectator: false,
		add_task_approver: false,
		delete_task_approver: false,
		revoke_approvals: false,
	},
	first_unread_comment_id: 0,
	last_activity: 0,
	unread_comments: 0,
	read_users: [],
} as TaskDataType;

const getDefaultState = (): State => {
	return {
		token: "",
		login: "",
		catalogs: [],
		catalogHeader: [],
		taskList: [],
		task: emptyTask,
		users: [],
		bots: [],
		departments: [],
		isError: false,
		currentUser: undefined,
		comments: [] as TaskComment[],
		formList: {},
		currentTaskParams: "",
		isNotificationGranted: false,
		formFields: [],
		currentUserInfo: null,
		appReady: true,
		forwardPathName: "/",
		currentMobileLocation: "",
		forms: [],
		unreadTaskCounter: 0,
		isCollapseMenu: true,
		queryParams: {},
		deepSearchQueryParams: {},
		deepSearchToggle: false,
		taskLeftScreen: "task",
		taskRightScreen: "chat",
		showSystemMessages: false,
		registrySettings: [],
		registryFilters: [],
		registryCurrentFormId: null,
		registrySort: null,
		registryQueryParams: {},
		registrySortType: {
			type: 1,
			ascending: 1,
		},
		isRegistryLoading: false,
		deepSearchTabs: [],
		isDeepSearched: false,
		tasksIdsForRead: [],
		appTitle: "",
		form: null,
		fieldsErrors: [],
		theme: "light",
		userSettings: null,
		taskListCache: {
			taskList: [],
			taskListSessionMap: null,
			timestamp: null,
		},
		taskChangedFields: [],
		editingFields: [],
		timestamp: null,
		scrollPosition: 0,
		storeCompany: {
			activityTab: "users",
			ascending: true,
			filter: 1,
			usersSortBy: 1,
		},
		notification: {
			message: null,
			timestamp: null,
		},
		warningSuccess: false,
		images: {},
		fromRegistry: false,
		isNewRowBlocked: false,
		taskSplitWidth: null,
		outsourceList: [],
		isMobile: false,
		mobileMenuIsOpen: false,
		desktopVersion: {
			currentScreenWidth: 0,
			isDesktopVersion: false,
		},
		PpaData: null,
		status: 'idle',
	} as State;
};

const EMPTY_FIELD_ERROR: FieldErrors = {
	id: 0,
	fields: [],
	isRequiredError: false,
	isValid: true,
	values: [],
};

const globalStore = createStore({
	plugins: [
		createPersistedState({
			reducer: (state) => {
				const { installerTable, containerWithSlider, installerSchedule, editingFields, ...rest } = state;
				return rest;
			},
		}),
	],
	state: getDefaultState(),
	mutations: {
		changeToken(state: State, payload: string) {
			state.token = payload;
		},
		changeCurrentUser(state: State, payload: CurrentUser) {
			state.currentUser = payload;
		},
		changeBot(state: State, payload: PostBotData & { avatar: string | null }) {
			state.bots = state.bots.map((bot) => {
				if (bot.id === payload.id) {
					return {
						...bot,
						avatar_url: payload.avatar ? payload.avatar : bot.avatar_url,
						info: {
							bot_name: payload.bot_name,
						},
					};
				} else {
					return bot;
				}
			});
		},
		changeDepartment(state: State, payload: { id: number; name: string | null; avatar: string | null }) {
			state.departments = state.departments.map((dep) => {
				if (dep.id === payload.id) {
					return {
						...dep,
						name: payload.name ? payload.name : dep.name,
						avatar_url: payload.avatar ? payload.avatar : dep.avatar_url,
					};
				} else {
					return dep;
				}
			});
		},
		changeMyProfile(state: State, payload: FullUser) {
			state.currentUserInfo = payload;
			state.users = state.users.map((user) => {
				if (user.id === payload.id) {
					return {
						...user,
						enabled: payload.enabled,
						avatar_url: payload.avatar_url,
						info: {
							name: payload.info.name,
							surname: payload.info.surname,
							work_phone: payload.info.work_phone,
						},
						department_ids: payload.department_ids,
					};
				} else {
					return user;
				}
			});
		},
		changeOtherProfile(
			state: State,
			payload: {
				id: number;
				name: string;
				surname: string;
				work_phone: string;
				department_ids: Array<number>;
			},
		) {
			state.users = state.users.map((user) => {
				if (user.id === payload.id) {
					return {
						...user,
						info: {
							name: payload.name,
							surname: payload.surname,
							work_phone: payload.work_phone,
						},
						department_ids: payload.department_ids,
					};
				} else {
					return user;
				}
			});
		},
		changeCatalog(state: State, payload: CatalogDataType) {
			const catalogIndex = state.catalogs.findIndex((catalog) => catalog.id === payload.id);
			if (catalogIndex !== -1) {
				state.catalogs[catalogIndex] = payload;
			} else {
				state.catalogs.push({ ...payload, timestamp: Date.now() });
			}
		},
		setCatalogs(state: State, payload: CatalogDataType[]) {
			state.catalogs = payload.map((catalog) => ({
				...catalog,
				timestamp: Date.now(),
			}));
		},
		setCatalogHeader(state: State, payload: CatalogListElementType[]) {
			state.catalogHeader = payload;
		},
		changeTask(state: State, payload: TaskDataType) {
			if (state.editingFields.length && state.task?.fields?.length && payload?.fields?.length) {
				state.task = {
					...payload,
					fields: preserveEditingFields(state.task.fields, payload.fields, state.editingFields),
				};
				return;
			}
			state.task = payload;
		},
		changeTaskSpectatorsUsers(state: State, payload: TaskSpectatorType[]) {
			state.task.spectators.users = payload;
		},
		changeTaskSpectatorsDepartments(state: State, payload: TaskSpectatorType[]) {
			state.task.spectators.departments = payload;
		},
		AddTaskAttachments(state: State, payload: TaskAttachment) {
			state.task.attachments.push(payload);
		},
		changeTasks(state: State, payload: TaskListElement[]) {
			if (!payload?.length) {
				state.taskList = [];
				return;
			}

			state.taskList = payload;
		},
		changeForms(state: State, payload: ListElementType[]) {
			if (!payload?.length) {
				state.formList = {};
				return;
			}
			for (const formListing of payload) {
				state.formList[formListing.id] = formListing;
			}
		},
		changeTaskListed(state: State, payload: TaskListElement) {
			state.taskList[payload.id] = payload;
		},
		changeFormListed(state: State, payload: ListElementType) {
			state.formList[payload.id] = payload;
		},
		changeComments(state: State, payload: TaskComment[]) {
			state.comments = payload;
		},
		pushComment(state: State, payload: TaskComment) {
			state.comments.push(payload);
		},
		editCommentAfterSocket(state: State, payload: { comment: TaskComment; index: number }) {
			state.comments[payload.index] = payload.comment;
		},
		deleteLastComment(state: State) {
			state.comments.pop();
		},
		changeUserDepartments(state: State, payload: unknown) {
			const users = (payload as User[]).filter((item) => item.type === "user");
			const bots = (payload as Bot[]).filter((item) => item.type === "bot");
			const departments = (payload as Department[]).filter((item) => {
				if ("name" in item) {
					return item;
				}
			});

			state.users = users;
			state.departments = departments;
			state.bots = bots;
		},
		changeIsError(state: State, payload: boolean) {
			state.isError = payload;
		},
		currentTaskParams(state: State, payload: string) {
			state.currentTaskParams = payload;
		},
		setTaskEmpty(state: State) {
			state.task = emptyTask;
			state.editingFields = [];
		},
		logout(state) {
			Object.assign(state, getDefaultState());
		},
		setNotificationPermission(state: State, payload: boolean) {
			state.isNotificationGranted = payload;
		},
		changeFormFields(state, payload: FieldsTypeValue[]) {
			state.formFields = payload;
		},
		changeFormFieldsById(state, payload: { id: number; value: FieldValue }) {
			const field = state.formFields.find((item) => item.id === payload.id);
			if (field) {
				field.value = payload.value;
			}
		},
		patchTask(state: State, payload: Partial<TaskDataType>) {
			Object.assign(state.task, payload);
		},
		setCurrentUserInfo(state: State, payload: FullUser) {
			if (state.currentUser?.id !== payload.id) {
				return;
			}
			state.currentUserInfo = payload;
		},
		setAppReady(state: State, payload: boolean) {
			state.appReady = payload;
		},
		changeForwardPathName(state: State, payload: string) {
			state.forwardPathName = payload;
		},
		setCurrentMobileLocation(state: State, payload: string) {
			state.currentMobileLocation = payload;
		},
		setForms(state: State, payload: ListElementType[]) {
			state.forms = payload;
		},
		setUnreadTaskCounter(state: State, payload: { unread_counter: number }) {
			state.unreadTaskCounter = payload.unread_counter;
		},
		incrementUnreadTaskCounter(state) {
			state.unreadTaskCounter++;
		},
		decrementUnreadTaskCounter(state) {
			state.unreadTaskCounter--;
		},
		setIsCollapseMenu(state: State, payload: boolean) {
			state.isCollapseMenu = payload;
		},
		setIsMobile(state: State, payload: boolean) {
			state.isMobile = payload;
		},
		setIsDesktopVersion(state: State, payload: boolean) {
			state.desktopVersion.isDesktopVersion = payload;
		},
		setCurrentUserWidth(state: State, payload: number) {
			state.desktopVersion.currentScreenWidth = payload;
		},
		setMobileMenuIsOpen(state: State, payload: boolean) {
			state.mobileMenuIsOpen = payload;
		},
		setQueryParams(state: State, payload: DeepSearchQueryParams) {
			state.queryParams = payload;
		},
		setDeepQueryParams(state: State, payload: DeepSearchQueryParams) {
			state.deepSearchQueryParams = payload;
		},
		clearQueryParams(state: State) {
			const params: DeepSearchQueryParams = {};

			if (state.queryParams["sort_type"]) {
				params.sort_type = state.queryParams["sort_type"];
			}

			state.queryParams = params;
		},
		clearDeepQueryParams(state: State) {
			state.deepSearchQueryParams = {};
		},
		setShowSystemMessages(state: State, payload: boolean) {
			state.showSystemMessages = payload;
		},
		setRegistrySetting(state: State, payload: RegistrySetting[]) {
			state.registrySettings = payload;
		},
		cleanRegistrySetting(state: State) {
			state.registrySettings = [];
		},
		setRegistryFilters(state: State, payload: RegistryFieldFilter) {
			const filterIndex = state.registryFilters.findIndex((filter) => filter.field_id === payload.field_id);
			if (filterIndex !== -1) {
				state.registryFilters[filterIndex] = payload;
			} else {
				state.registryFilters.push(payload);
			}
		},
		setRegistryQueryParams(state: State, payload: DeepSearchQueryParams) {
			state.registryQueryParams = payload;
		},
		resetRegistryFilters(state: State, payload: { id: number; filterType: RegistryFilterParamsKeys }) {
			const filters = state.registryFilters.map((filter) => {
				if (filter.field_id === payload.id) {
					delete filter[payload.filterType];
					return filter;
				}
				return filter;
			});

			state.registryFilters = filters.filter((filter) => {
				for (const filterKey in filter) {
					const key = filterKey as keyof typeof filter;
					if (key !== "field_id" && filter[key] !== undefined) {
						return true;
					}
				}
				return false;
			});
		},
		resetRegistryFieldFilters(state: State, payload: { id: number }) {
			state.registryFilters = state.registryFilters.filter((filter) => filter.field_id === payload.id);
		},
		clearRegistryFilters(state: State) {
			state.registryFilters = [];
		},
		setRegistrySort(state: State, payload: RegistryFieldSorting) {
			state.registrySortType = {
				type: 3,
			};
			state.registrySort = payload;
		},
		clearRegistrySort(state: State) {
			state.registrySortType = {
				type: 1,
				ascending: 1,
			};
			state.registrySort = null;
		},
		updateRegistrySortType(state: State, payload: RegistrySortType) {
			state.registrySortType = payload;
		},
		registryLoading(state: State) {
			state.isRegistryLoading = true;
		},
		registryLoaded(state: State) {
			state.isRegistryLoading = false;
		},
		changeTaskLeftSide(state: State, payload: string) {
			state.taskLeftScreen = payload;
		},
		changeTaskRightSide(state: State, payload: string) {
			state.taskRightScreen = payload;
		},
		setDeepSearchTabs(state: State, payload: string[]) {
			state.deepSearchTabs = payload;
		},
		setDeepSearched(state: State, payload: boolean) {
			state.isDeepSearched = payload;
		},
		setTasksForRead(state: State, payload: number[]) {
			state.tasksIdsForRead = payload;
		},
		addTaskForRead(state: State, payload: number) {
			const foundTaskId = state.tasksIdsForRead.find((id) => id === payload);
			if (foundTaskId) return;
			state.tasksIdsForRead.push(payload);
		},
		deleteTaskForRead(state: State, payload: number) {
			state.tasksIdsForRead = state.tasksIdsForRead.filter((taskId) => taskId !== payload);
		},
		clearTaskForRead(state: State) {
			state.tasksIdsForRead = [];
		},
		setAppTitle(state: State, payload: { hint: string; id?: number; name?: string }) {
			const newTitle = updateTitle<State>(state, payload.hint, payload.id, payload.name);
			state.appTitle = newTitle;
			document.title = newTitle;
		},
		setForm(state: State, payload: FormDataType<FieldsType>) {
			state.form = payload;
		},
		clearForm(state: State) {
			state.form = null;
		},
		setRegistryCurrentFormId(state: State, payload: number) {
			state.registryCurrentFormId = payload;
		},
		setFieldErrors(state: State, payload: FieldErrors[]) {
			state.fieldsErrors = getAllFieldsErrors(payload);
		},
		clearFieldErrors(state: State) {
			state.fieldsErrors = [];
		},
		updateFieldErrors(state: State, payload: FieldErrors) {
			state.fieldsErrors = state.fieldsErrors.map((fieldsError) => {
				if (
					(!payload.rowId && fieldsError.id === payload.id) ||
					(payload.rowId === fieldsError.rowId && fieldsError.id === payload.id)
				) {
					return payload;
				}
				return fieldsError;
			});
		},
		updateTableFieldErrors(state: State, payload: { fieldErrors: FieldErrors; rowId: number }) {
			state.fieldsErrors = state.fieldsErrors.map((fieldsError) => {
				if (
					(!payload.rowId && fieldsError.id === payload.fieldErrors.id) ||
					(payload.rowId === fieldsError.rowId && fieldsError.id === payload.fieldErrors.id)
				) {
					return payload.fieldErrors;
				}
				return fieldsError;
			});
		},
		removeTableFieldErrors(state: State, payload: { rowId: number | null; fieldId: number }) {
			state.fieldsErrors = state.fieldsErrors.filter(
				(field) => field.rowId === payload.rowId && field.id === payload.fieldId,
			);
		},
		setTheme(state: State, payload: 1 | 0) {
			state.theme = payload === 1 ? "light" : "dark";
		},
		setUserSettings(state: State, payload: UserSettings) {
			state.userSettings = payload;
		},
		clearTaskListSession(state: State) {
			state.taskListCache = {
				taskList: [],
				taskListSessionMap: null,
				timestamp: null,
			};
		},
		setTaskChangedFields(state: State, payload: ChangeField[]) {
			state.taskChangedFields = payload;
		},
		addEditingField(state: State, payload: EditingFieldRef) {
			const exists = state.editingFields.some(
				(field) => field.field_id === payload.field_id && field.row_id === payload.row_id,
			);
			if (!exists) {
				state.editingFields.push(payload);
			}
		},
		removeEditingField(state: State, payload: EditingFieldRef) {
			state.editingFields = state.editingFields.filter(
				(field) => !(field.field_id === payload.field_id && field.row_id === payload.row_id),
			);
		},
		removeRowFields(state: State, payload: number) {
			const rowId = payload;
			state.taskChangedFields = state.taskChangedFields.filter((row) => row.row_id !== rowId);
		},
		setTaskList(state: State, payload: TaskListElement[]) {
			state.taskListCache.taskList = payload;
		},
		setTimeStamp(state: State, paylaod: number | null) {
			state.timestamp = paylaod;
		},
		setScrollPosition(state: State, payload: number) {
			state.scrollPosition = payload;
		},
		setNotification(state: State, payload: TaskComment<number>) {
			state.notification = {
				message: payload,
				timestamp: Date.now(),
			};
		},
		clearNotification(state: State) {
			state.notification = {
				message: null,
				timestamp: null,
			};
		},
		toggleDeepSearch(state: State, payload: boolean) {
			state.deepSearchToggle = payload;
		},
		setCompanyAscending(state: State, payload: boolean) {
			state.storeCompany.ascending = payload;
		},
		setCompanyActivityTab(state: State, payload: "users" | "departments" | "bots") {
			state.storeCompany.activityTab = payload;
		},
		setCompanyFilter(state: State, payload: 1 | 2) {
			state.storeCompany.filter = payload;
		},
		setCompanyUsersSortBy(state: State, payload: 1 | 2) {
			state.storeCompany.usersSortBy = payload;
		},
		changeWarningStatus(state: State) {
			state.warningSuccess = true;
			setTimeout(() => {
				state.warningSuccess = false;
			}, 2500);
		},
		setImage(state: State, payload: { url: string; base64String: string }) {
			if (!state.images[payload.url]) {
				state.images[payload.url] = payload.base64String;
			}
		},
		clearImages(state: State) {
			state.images = {};
		},
		setFromRegistry(state: State, payload: boolean) {
			state.fromRegistry = payload;
		},
		setIsNewRowBlocked(state: State, payload: boolean) {
			state.isNewRowBlocked = payload;
		},
		setTaskSplitWidth(state: State, payload: number | null) {
			state.taskSplitWidth = payload;
		},
		setOutsourceList(state: State, payload: IOutsourcer[]) {
			state.outsourceList = payload;
		},
		setPpaDate(state: State, paylaod: PpaDate){
			state.PpaData = paylaod;
		},
		setPpaStatus(state: State, paylaod: string){
			state.status = paylaod;
		}
	},
	actions: {
		setIsNewRowBlocked({ commit }: { commit: Commit }, payload: boolean) {
			commit("setIsNewRowBlocked", payload)
		},
		changeToken({ commit }: { commit: Commit }, payload: string) {
			commit("changeToken", payload);
		},
		changeCurrentUser({ commit }: { commit: Commit }, payload: CurrentUser) {
			commit("changeCurrentUser", payload);
		},
		changeCatalog({ commit }: { commit: Commit }, payload: CatalogDataType) {
			commit("changeCatalog", payload);
		},
		setCatalogHeader({ commit }: { commit: Commit }, payload: CatalogListElementType) {
			commit("setCatalogHeader", payload);
		},
		setIsCollapseMenu({ commit }: { commit: Commit }, payload: boolean) {
			commit("setIsCollapseMenu", payload);
		},
		setIsMobile({ commit }: { commit: Commit }, payload: boolean) {
			commit("setIsMobile", payload);
		},
		setIsDesktopVersion({ commit }: { commit: Commit }, payload: boolean) {
			commit("setIsDesktopVersion", payload);
		},
		setCurrentUserWidth({ commit }: { commit: Commit }, payload: boolean) {
			commit("setCurrentUserWidth", payload);
		},
		setMobileMenuIsOpen({ commit }: { commit: Commit }, payload: boolean) {
			commit("setMobileMenuIsOpen", payload);
		},
		setChangeTask({ commit }: { commit: Commit }, payload: TaskDataType) {
			commit("changeTask", payload);
		},
		changeTask({ commit }: { commit: Commit }, payload: TaskDataType) {
			commit("changeTask", payload);
			commit("setAppTitle", { hint: "Task", name: payload.name });
		},
		changeTaskSpectatorsUsers({ commit }: { commit: Commit }, payload: TaskSpectatorType[]) {
			commit("changeTaskSpectatorsUsers", payload);
		},
		changeTaskSpectatorsDepartments({ commit }: { commit: Commit }, payload: TaskSpectatorType[]) {
			commit("changeTaskSpectatorsDepartments", payload);
		},
		changeTasks({ commit }: { commit: Commit }, payload: TaskListElement[]) {
			commit("changeTasks", payload);
		},
		changeForms({ commit }: { commit: Commit }, payload: ListElementType[]) {
			commit("changeForms", payload);
		},
		changeTaskListed({ commit }: { commit: Commit }, payload: TaskListElement) {
			commit("changeTaskListed", payload);
		},
		changeFormListed({ commit }: { commit: Commit }, payload: ListElementType) {
			commit("changeFormListed", payload);
		},
		changeUserDepartments({ commit }: { commit: Commit }, payload: unknown) {
			commit("changeUserDepartments", payload);
		},
		changeIsError({ commit }: { commit: Commit }, payload: unknown) {
			commit("changeIsError", payload);
		},
		currentTaskParams({ commit }: { commit: Commit }, payload: unknown) {
			commit("currentTaskParams", payload);
		},
		async changeTaskAPI({ commit }: { commit: Commit }, payload: number) {
			const task = await getTask(payload);
			commit("changeTask", task);
			commit("setIsNewRowBlocked", false);
		},
		async changeCommentsAPI({ commit }: { commit: Commit }, payload: TaskCommentsPayload) {
			const comments = await getTaskComments(payload);
			commit("changeComments", comments);
		},
		changeComments({ commit }: { commit: Commit }, payload: unknown) {
			commit("changeComments", payload);
		},
		pushComment({ commit }: { commit: Commit }, payload: unknown) {
			commit("pushComment", payload);
		},
		AddTaskAttachments({ commit }: { commit: Commit }, payload: unknown) {
			commit("AddTaskAttachments", payload);
		},
		async authorizeUser({ commit }: { commit: Commit }, payload) {
			try {
				commit("setAppReady", false);
				const response = await authorize(payload.login, payload.password);
				const currentUser = {
					login: payload.login,
					id: response.user_id,
				};
				commit("changeCurrentUser", currentUser);
				commit("changeToken", response.token);
				await getUsersDepartments();

				const userInfo = await getUserInfoById(currentUser.id);
				commit("setCurrentUserInfo", userInfo);
			} catch (error) {
				commit("logout");
				if (error instanceof TooManyRequestsError) {
					throw error;
				}
			}
		},
		setTaskEmpty({ commit }: { commit: Commit }) {
			commit("setTaskEmpty");
		},
		logout({ commit }: { commit: Commit }) {
			commit("logout");
		},
		setNotificationPermission({ commit }: { commit: Commit }, payload: boolean) {
			commit("setNotificationPermission", payload);
		},
		changeFormFields({ commit }: { commit: Commit }, payload: FieldsTypeValue[]) {
			commit("changeFormFields", payload);
		},
		changeFormFieldsById({ commit }: { commit: Commit }, payload: { id: number; value: FieldValue }) {
			commit("changeFormFieldsById", payload);
		},
		setAppReady({ commit }: { commit: Commit }, payload: boolean) {
			commit("setAppReady", payload);
		},
		changeForwardPathName({ commit }: { commit: Commit }, payload: string) {
			commit("changeForwardPathName", payload);
		},
		toggleDeepSearch({ commit }: { commit: Commit }, payload: boolean) {
			commit("toggleDeepSearch", payload);
		},
		setCurrentUserInfo({ commit }: { commit: Commit }, payload: FullUser) {
			commit("setCurrentUserInfo", payload);
		},
		deleteLastComment({ commit }: { commit: Commit }) {
			commit("deleteLastComment");
		},
		setForms({ commit }: { commit: Commit }, payload: ListElementType[]) {
			commit("setForms", payload);
		},
		setQueryParams({ commit }: { commit: Commit }, payload: DeepSearchQueryParams) {
			commit("setQueryParams", payload);
		},
		setDeepQueryParams({ commit }: { commit: Commit }, payload: DeepSearchQueryParams) {
			commit("setDeepQueryParams", payload);
		},
		clearQueryParams({ commit }: { commit: Commit }) {
			commit("clearQueryParams");
		},
		clearDeepQueryParams({ commit }: { commit: Commit }) {
			commit("clearDeepQueryParams");
		},
		changeBot({ commit }: { commit: Commit }, payload: PostBotData & { avatar: string | null }) {
			commit("changeBot", payload);
		},
		changeDepartment(
			{ commit }: { commit: Commit },
			payload: {
				id: number;
				name: string | null;
				avatar: string | null;
			},
		) {
			commit("changeDepartment", payload);
		},
		changeMyProfile({ commit }: { commit: Commit }, payload: FullUser) {
			commit("changeMyProfile", payload);
		},
		changeOtherProfile(
			{ commit }: { commit: Commit },
			payload: {
				id: number;
				name: string;
				surname: string;
				work_phone: string;
				department_ids: Array<number>;
			},
		) {
			commit("changeOtherProfile", payload);
		},
		changeTaskLeftSide({ commit }: { commit: Commit }, payload: string) {
			commit("changeTaskLeftSide", payload);
		},
		changeTaskRightSide({ commit }: { commit: Commit }, payload: string) {
			commit("changeTaskRightSide", payload);
		},
		setForm({ commit }: { commit: Commit }, payload: FormDataType<FieldsType>) {
			commit("setForm", payload);
			commit("setAppTitle", { hint: "Form" });
		},
		setUserSettings({ commit }: { commit: Commit }, payload: UserSettings) {
			commit("setUserSettings", payload);
			commit("setTheme", payload.ui_theme);
		},
		setTaskList({ commit }: { commit: Commit }, payload: TaskListElement[]) {
			commit("setTaskList", payload);
		},
		async setTaskListSession({ state }: { commit: Commit; state: State }, payload: TaskListElement[]) {
			if (!payload.length) return;
			const encodedData = await createLatinHash(replaceHtmlEntities(JSON.stringify(state.queryParams)));
			const taskIds = payload.map((task) => task.id);
			if (!state.taskListCache.taskListSessionMap) {
				state.taskListCache.taskListSessionMap = {
					[encodedData]: taskIds,
				};

				state.taskListCache.timestamp = Date.now();
			} else {
				state.taskListCache.taskListSessionMap[encodedData] = taskIds;
			}
		},
		updateStoreCompany({ commit }, payload: StoreCompany) {
			commit("setCompanyAscending", payload.ascending);
			commit("setCompanyActivityTab", payload.activityTab);
			commit("setCompanyFilter", payload.filter);
			commit("setCompanyUsersSortBy", payload.usersSortBy);
		},
		updateDesktopVersion({ commit }, payload: DesktopVersion) {
			commit("setIsDesktopVersion", payload.isDesktopVersion);
			commit("setCurrentUserWidth", payload.currentScreenWidth);
		},
		setOutsourceList({ commit }, payload: IOutsourcer[]) {
			commit("setOutsourceList", payload);
		},

		changePpa({ commit }, paylaod: PpaDate){
			commit('setPpaDate', paylaod);
			commit('setPpaStatus', 'success');
		}
	},
	getters: {
		getToken(state: State) {
			return state.token;
		},
		getUsername(state: State) {
			if (state.currentUser) {
				return state.currentUser.login;
			}
			return undefined;
		},
		getCurrentUser(state: State) {
			return state.currentUser;
		},
		getCatalogs(state: State) {
			return state.catalogs;
		},
		getCatalogsHeader(state: State) {
			return state.catalogHeader;
		},
		getDeepSearchToggle(state: State) {
			return state.deepSearchToggle;
		},
		getTask(state: State) {
			return state.task;
		},
		getComments(state: State) {
			return state.comments;
		},
		getTaskListed: (state: State) => (id: number) => {
			return state.taskList[id];
		},
		getFormListed: (state: State) => (id: number) => {
			return state.formList[id];
		},
		getTasks(state: State) {
			return Object.values(state.taskList);
		},
		getCurrentStage(state: State) {
			return state.task.step_id;
		},
		isAuth(state: State) {
			return !!state.token;
		},
		getUsers(state: State) {
			return state.users;
		},
		getDepartments(state: State) {
			return state.departments;
		},
		getBots(state: State) {
			return state.bots;
		},
		getFormField(state: State) {
			return state.task.fields;
		},
		isError(state: State) {
			return state.isError;
		},
		currentTaskParams(state: State) {
			return state.currentTaskParams;
		},
		isNotificationGranted(state: State) {
			return state.isNotificationGranted;
		},
		getFormFields(state: State) {
			return state.formFields;
		},
		getFormFieldByID: (state: State) => (id: number) => {
			return state.formFields.find((field) => field.id === id);
		},
		getCurrentUserInfo(state) {
			return state.currentUserInfo;
		},
		isAppReady(state) {
			return state.appReady;
		},
		getForwardPathName(state) {
			return state.forwardPathName;
		},
		getCurrentMobileLocation(state) {
			return state.currentMobileLocation;
		},
		getForms(state) {
			return state.forms;
		},
		getUnreadTaskCounter(state) {
			return state.unreadTaskCounter;
		},
		getDesktopVersion(state) {
			return state.desktopVersion;
		},
		getIsMobile(state) {
			return state.isMobile;
		},
		getIsCollapseMenu(state) {
			return state.isCollapseMenu;
		},
		getQueryParams(state) {
			return state.queryParams;
		},
		getDeepQueryParams(state) {
			return state.deepSearchQueryParams;
		},
		getShowSystemMessages(state) {
			return state.showSystemMessages;
		},
		getRegistrySetting(state) {
			return state.registrySettings;
		},
		getRegistryFilters(state: State): RegistryFieldFilter[] {
			return state.registryFilters;
		},
		getRegistryFilterById: (state: State) => (id: number) => {
			return state.registryFilters.find((filter) => filter.field_id === id) || {};
		},
		getRegistrySort(state: State) {
			return state.registrySort;
		},
		getRegistrySortType(state: State) {
			return state.registrySortType;
		},
		getIsRegistryLoading(state: State) {
			return state.isRegistryLoading;
		},
		getTaskLeftSide(state: State) {
			return state.taskLeftScreen;
		},
		getTaskRightSide(state: State) {
			return state.taskRightScreen;
		},
		getDeepSearchTabs(state: State) {
			return state.deepSearchTabs;
		},
		isDeepSearched(state: State) {
			return state.isDeepSearched;
		},
		getTasksIdsForRead(state: State) {
			return state.tasksIdsForRead;
		},
		getTaskIdForReadById: (state: State) => (id: number) => {
			return state.tasksIdsForRead.find((taskId) => taskId === id) || null;
		},
		getAppTitle(state: State) {
			return state.appTitle;
		},
		getForm(state: State) {
			return state.form;
		},
		getRegistryCurrentFormId(state: State) {
			return state.registryCurrentFormId;
		},
		getFieldErrors(state: State): FieldErrors[] {
			return state.fieldsErrors;
		},
		getFieldErrorsMap(state: State): Map<number, FieldErrors> {
			const errorsMap = new Map<number, FieldErrors>();
			for (const fieldError of state.fieldsErrors) {
				if (!errorsMap.has(fieldError.id)) {
					errorsMap.set(fieldError.id, fieldError);
				}
			}
			return errorsMap;
		},
		getFieldErrorById: (_state: State, getters: { getFieldErrorsMap: Map<number, FieldErrors> }) => {
			return (payload: { fieldId: number }) => {
				return getters.getFieldErrorsMap.get(payload.fieldId) || EMPTY_FIELD_ERROR;
			};
		},
		getTableFieldErrorById: (state: State) => (payload: { fieldId: number; rowId: number }) => {
			return (
				state.fieldsErrors.find(
					(fieldError) => fieldError.id === payload.fieldId && fieldError.rowId === payload.rowId,
				) || EMPTY_FIELD_ERROR
			);
		},
		getTheme(state: State) {
			return state.theme;
		},
		getUserSettings(state: State) {
			return state.userSettings;
		},
		getTaskListCache(state: State) {
			return state.taskListCache;
		},
		getTaskChangedFields(state: State) {
			return state.taskChangedFields;
		},
		getEditingFields(state: State) {
			return state.editingFields;
		},
		getTimestamp(state: State) {
			return state.timestamp;
		},
		getScrollPosition(state: State) {
			return state.scrollPosition;
		},
		getNotification(state: State) {
			return state.notification;
		},
		getStoreCompany(state: State) {
			return state.storeCompany;
		},
		getRegistryQueryParams(state: State) {
			return state.registryQueryParams;
		},
		getWarningStatus(state: State) {
			return state.warningSuccess;
		},
		getImages(state: State) {
			return state.images;
		},
		getFromRegistry(state: State) {
			return state.fromRegistry;
		},
		getIsNewRowBlocked(state: State) {
			return state.isNewRowBlocked;
		},
		getTaskSplitWidth(state: State) {
			return state.taskSplitWidth;
		},
		getOutsourceList(state: State) {
			return state.outsourceList;
		},
		getMobileMenuIsOpen(state: State) {
			return state.mobileMenuIsOpen;
		},
		getPpaData(state: State){
			return state.PpaData;
		},
		getPpaStatus(state: State){
			return state.status;
		},
	},

	modules: {
		installerTable: installerTableModule,
		containerWithSlider: containerWithSliderModule,
	},
});

export default globalStore;
